package uk.co.nickthecoder.glok.backend.webgl

import org.khronos.webgl.WebGLRenderingContext.Companion.FLOAT
import org.khronos.webgl.WebGLRenderingContext.Companion.TRIANGLE_STRIP
import uk.co.nickthecoder.glok.scene.Color
import uk.co.nickthecoder.glok.util.Matrix

internal class FillCircleProgram private constructor(): UVProgram(VERTEX, FRAGMENT) {

    private val colorLocation = getUniformLocation("color")
    private val radiusLocation = getUniformLocation("radius")

    private fun begin(viewMatrix: Matrix, color: Color, radius: Float) {
        begin(viewMatrix)
        setUniform(colorLocation, color)
        setUniform(radiusLocation, radius)
    }

    override fun enableAttributes() {
        with(WebGLBackend.gl) {
            vertexAttribPointer(positionLocation, 2, FLOAT, false, 16, 0)
            vertexAttribPointer(uvLocation, 2, FLOAT, false, 16, 8)
            enableVertexAttribArray(positionLocation)
            enableVertexAttribArray(uvLocation)
        }
    }

    fun fillCircle(centerX: Float, centerY: Float, radius: Float) {
        drawTriangles(
            arrayOf<Float>(
                centerX + radius, centerY + radius, radius, + radius,
                centerX - radius, centerY + radius, - radius, radius,
                centerX + radius, centerY - radius, radius, - radius,
                centerX - radius, centerY - radius, - radius, - radius
            ), TRIANGLE_STRIP
        )
        WebGLBackend.gl.disableVertexAttribArray(uvLocation)
    }

    fun fillQuarterCircle(radius: Float, centerX: Float, centerY: Float, cornerX: Float, cornerY: Float) {
        // log.info("Fill quarter circle $centerX,$centerY -> $cornerX,$cornerY radius : $radius")
        drawTriangles(
            arrayOf<Float>(
                centerX, centerY, 0f, 0f,
                cornerX, centerY, radius, 0f,
                centerX, cornerY, 0f, radius,
                cornerX, cornerY, radius, radius
            ), TRIANGLE_STRIP
        )
        WebGLBackend.gl.disableVertexAttribArray(uvLocation)
    }

    companion object {

        private val instance = FillCircleProgram()

        fun use(viewMatrix: Matrix, color: Color, radius: Float, block: FillCircleProgram.() -> Unit) {
            instance.begin(viewMatrix, color, radius)
            instance.block()
            instance.end()
        }

    }
}

private const val VERTEX = """
uniform mat3 viewMatrix;
attribute vec2 position;
attribute vec2 uv;

varying vec2 local;

void main() {
    local = uv;
    gl_Position = vec4( viewMatrix * vec3(position, 1.0), 1.0);
}
"""

private const val FRAGMENT = """
precision highp float;

varying vec2 local;

uniform vec4 color;
uniform float radius;

void main() {
    float dist = length(local);
    float t = clamp(dist - radius + 0.5, 0.0, 1.0 );
    gl_FragColor = vec4( color.xyz, color.w * (1.0-t) );
}
"""
